import {
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/frontend-plugin-api';
import {
  Configuration,
  InternalGraphEnginesApi,
} from '../graph-engine-api-sdk';
import type {
  GraphEngine,
  GraphEngineApi,
  PatchGraphEnginesResult,
} from '../types';

export class GraphEngineClient implements GraphEngineApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly oktaAuthApi: OAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    oktaAuthApi: OAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.oktaAuthApi = options.oktaAuthApi;
  }

  private async buildApi(): Promise<InternalGraphEnginesApi> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const oktaToken = await this.oktaAuthApi.getAccessToken();
    const configuration = new Configuration({
      basePath: `${proxyBaseUrl}/graph-engine-api`,
      fetchApi: this.fetchApi.fetch,
      headers: {
        'Aura-Portal-Okta-Token': oktaToken,
      },
    });
    return new InternalGraphEnginesApi(configuration);
  }

  async listGraphEngines(showDeleted: boolean): Promise<GraphEngine[]> {
    const api = await this.buildApi();
    return api.listGraphEnginesInternal({ showDeleted });
  }

  async getGraphEngine(id: string): Promise<GraphEngine> {
    const api = await this.buildApi();
    // includeDataModel returns the persisted graph model schema, shown on the
    // details page. It is omitted from the list endpoint to keep it light.
    return api.getGraphEnginePortal({ id, includeDataModel: true });
  }

  async patchGraphEngineInternal(
    id: string,
    body: { memory?: string; neo4jVersion?: string },
  ): Promise<void> {
    const api = await this.buildApi();
    await api.patchGraphEngineInternal({
      id,
      request: body,
    });
  }

  async patchGraphEnginesInternal(body: {
    ids: string[];
    memory?: string;
    neo4jVersion?: string;
  }): Promise<PatchGraphEnginesResult> {
    const api = await this.buildApi();
    const response = await api.patchGraphEnginesInternal({
      request: body,
    });
    return {
      results: (response.results ?? []).map(result => ({
        id: result.id ?? '',
        success: result.success ?? false,
        error: result.error,
      })),
    };
  }
}
